import Slider from 'react-slick';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

interface UploadedImageProps {
  item: any;
  index: number;
  previewUrl: string;
  name: string;
  setLightboxSlide: any;
  setLightboxIndex: any;
  removeFile: any;
}

interface ImageSliderProps {
  selectedFiles: any;
  name: string;
  setLightboxSlide: any;
  setLightboxIndex: any;
  removeFile: any;
}

const UploadedImage: React.FC<UploadedImageProps> = ({
  item,
  index,
  previewUrl,
  name,
  setLightboxSlide,
  setLightboxIndex,
  removeFile,
}) => {
  return (
    <TransformWrapper
      initialScale={1}
      initialPositionX={200}
      initialPositionY={100}
    >
      <div className="text-center">
        <div
          className="fit-image-container position-relative"
          onClick={() => {
            setLightboxIndex(index);
            setLightboxSlide(
              item.allFiles.map((file: any) => ({
                src: file.previewUrl,
                alt: file.name,
              }))
            );
          }}
        >
          <div className="d-grid">
            <TransformComponent>
              <img src={previewUrl} alt={`Car Image ${index + 1}`} />
            </TransformComponent>
          </div>
        </div>

        <div className="mt-2 filename-display">
          <small>{item.name}</small>
          {name !== '' && (
            <span
              className="ms-1 text-danger"
              onClick={() => removeFile(item.name, name)}
            >
              <i className="feather icon-x-circle" />
            </span>
          )}
        </div>
      </div>
    </TransformWrapper>
  );
};

const ImageSlider: React.FC<ImageSliderProps> = ({
  selectedFiles,
  name,
  removeFile,
  setLightboxSlide,
  setLightboxIndex,
}) => {
  const [nav, setNav] = useState<any>(null);
  const sliderRef = useRef<any>(null);

  useEffect(() => {
    setNav(sliderRef.current);
  }, []);

  const settings = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    asNavFor: nav || undefined,
    ref: (slider: any) => (sliderRef.current = slider),
  };

  // Memoized URLs to prevent repeated createObjectURL calls
  const processedFiles = useMemo(() => {
    return selectedFiles?.map((item: any) => {
      const previewUrl =
        item?.file_url ?? (item?.file ? URL.createObjectURL(item.file) : '');
      return {
        ...item,
        previewUrl,
        allFiles: selectedFiles.map((f: any) => ({
          previewUrl:
            f?.file_url ?? (f?.file ? URL.createObjectURL(f.file) : ''),
          name: f.name,
        })),
      };
    });
  }, [selectedFiles]);

  useEffect(() => {
    return () => {
      // Revoke all object URLs on unmount to avoid memory leaks
      processedFiles?.forEach((item: any) => {
        if (item?.file && !item?.file_url) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, [processedFiles]);

  return processedFiles?.length > 1 ? (
    <Slider {...settings} className="image-slider mb-1">
      {processedFiles.map((item: any, index: number) => (
        <div key={index}>
          <UploadedImage
            item={item}
            index={index}
            previewUrl={item.previewUrl}
            name={name}
            setLightboxSlide={setLightboxSlide}
            setLightboxIndex={setLightboxIndex}
            removeFile={removeFile}
          />
        </div>
      ))}
    </Slider>
  ) : (
    processedFiles?.map((item: any, index: number) => (
      <div key={index} className="image-slider">
        <UploadedImage
          item={item}
          index={index}
          previewUrl={item.previewUrl}
          name={name}
          setLightboxSlide={setLightboxSlide}
          setLightboxIndex={setLightboxIndex}
          removeFile={removeFile}
        />
      </div>
    ))
  );
};

export default ImageSlider;
